-- | This module implements a monadic way of querying GraphQL
module GraphQL.Join where

import Common

import Affjax.RequestBody as AXR
import Affjax.ResponseFormat as ResponseFormat
import Affjax.Web as AW
import Control.Monad.Except (ExceptT(..), except, runExceptT)
import Data.Argonaut (class DecodeJson, class EncodeJson, decodeJson)
import Data.Array ((!!))
import Data.Array as Arr
import Data.Either (note)
import Data.HTTP.Method (Method(..))
import GraphQL (batchFileQuery, graphqlEndpoint, handleResult, query)
import Prim.Row as Row
import Prim.RowList as RL
import Record as Rec
import Type.Prelude (class IsSymbol, Proxy(..))
import Web.File.Blob (Blob)
import Web.XHR.FormData (FileName)

-- @TODO: change this to include uploads :: Array GQLUpload
type GQLQuery = { query :: String, variables :: Json }

type GQLUpload =
    { path :: String
    , name :: Maybe FileName
    , file :: Blob
    }

class GraphQL q r | q -> r where
    runGraphQL :: q -> Aff (Either ErrorWrapper r)

runGraphQL_ :: forall q r. GraphQL q r => q -> ExceptT ErrorWrapper Aff r
runGraphQL_ = ExceptT <<< runGraphQL

-- | Data structure wrapping a single GraphQL query and a decoder function
data StringQuery a = StringQuery
    { query :: GQLQuery
    , decode :: Json -> Either ErrorWrapper a
    }

instance showSQ :: Show (StringQuery a) where
    show (StringQuery { query }) = "Query: " <> query.query

instance stringGQL :: GraphQL (StringQuery a) a where
    -- | Run the given GraphQL query and produce either its result or an error
    runGraphQL :: StringQuery a -> Aff (Either ErrorWrapper a)
    runGraphQL (StringQuery { query: { query: qstr, variables }, decode }) = runExceptT do
        ret <- ExceptT $ query qstr variables
        except $ decode ret
instance singleJoinable :: JoinableQuery (StringQuery a) a where
    getQuery (StringQuery { query }) = query
    getDecode (StringQuery { decode }) = decode



-- | Construct a GraphQL query
gqlQuery' :: forall a. String -> Json -> (Json -> Either ErrorWrapper a) -> StringQuery a
gqlQuery' qstr vars decode = StringQuery { query: { query: qstr, variables: vars }, decode }


-- | Construct a GraphQL query, encoding vars as json
gqlQuery :: forall a. forall vars. EncodeJson vars =>
    String -> vars -> (Json -> Either ErrorWrapper a) -> StringQuery a
gqlQuery qstr vars decode = gqlQuery' qstr (encodeJson vars) decode

-- | For queries which return a single object, this extracts the object from the key based on `qname`
decodeQuery :: forall a. forall vars. DecodeJson a => EncodeJson vars =>
    String -> String -> vars -> StringQuery a
decodeQuery qname qstr vars = decodeQuery_ qstr vars
    (\j -> wrapJsonError' j $ decodeJObject j >>= (_ .: "data") >>= (_ .: qname))

-- | Easy query function for results with a `DecodeJson` instance
decodeQueryData :: forall a. forall vars. DecodeJson a => EncodeJson vars =>
    String -> vars -> StringQuery a
decodeQueryData qstr vars = decodeQuery_ qstr vars (\j -> wrapJsonError' j $ decodeJObject j >>= (_ .: "data"))

-- | Like `decodeQuery` with a function to manipulate the json before passing it to the `DecodeJson` instance
decodeQuery_ :: forall a. forall vars. DecodeJson a => EncodeJson vars =>
    String -> vars -> (Json -> Either ErrorWrapper Json) -> StringQuery a
decodeQuery_ qstr vars decode' = gqlQuery qstr vars decode
  where
    decode json = do
       json' <- decode' json
       wrapJsonError' json' $ decodeJson json'


-- | Class for queries that can be joined together
class JoinableQuery q r | q -> r where
    getQuery :: q -> GQLQuery
    getDecode :: q -> Json -> Either ErrorWrapper r

-- | Run multiple GraphQL queries at once
-- |
-- | Queries should be passed to this as a record, and this function will
-- | return a record with matching keys, but with the results of each query as
-- | the values
-- |
-- | All queries being combined must implement the `JoinableQuery` class
-- |
-- | NOTE: Currently, the compiler will not check that all items in the output
-- | are actually in the input, which means code like the following will type
-- | check, despite the function not returning `bar`
-- | ```
-- | { foo, bar } <- multiQuery { foo: fooQuery }
-- | -- bar is `undefined` at runtime but the compiler will infer it to be
-- | -- `Int` anyway
-- | let bar' = bar + 1 
-- | ```
-- | Take care to avoid doing this.
multiQuery :: forall i o il. RL.RowToList i il => JoinQueries i o il =>
    Record i -> Aff (Either ErrorWrapper (Record o))
multiQuery queries = runExceptT $ multiQuery_ queries

-- | `ExceptT` version of `multiQuery`
multiQuery_ :: forall i o il. RL.RowToList i il => JoinQueries i o il =>
    Record i -> ExceptT ErrorWrapper Aff (Record o)
multiQuery_ queries = do
    ret <- runMultiQuery $ jqQueries proxy queries
    except $ jqDecode proxy queries 0 ret
  where 
    proxy :: Proxy il
    proxy = Proxy


runMultiQuery :: Array GQLQuery -> ExceptT ErrorWrapper Aff (Array Json)
runMultiQuery queries = ExceptT do
    let content = encodeJson queries
    res <- AW.request $ AW.defaultRequest {
        method = Left POST,
        url = graphqlEndpoint,
        content = Just $ AXR.Json content,
        responseFormat = ResponseFormat.json,
        withCredentials = true
    }
    resE <- handleResult res
    pure (resE >>= (\j -> wrapJsonError $ decodeJson j))

-- @TODO: once GQLQuery supports uploads, merge this with `runMultiQuery`.
runMultiQueryWithUpload :: Array GQLQuery -> GQLUpload -> ExceptT ErrorWrapper Aff (Array Json)
runMultiQueryWithUpload queries upload = ExceptT do
    resE <- batchFileQuery queries upload.path upload.name upload.file
    pure (resE >>= (\j -> wrapJsonError $ decodeJson j))


-- Danger: type-level programming below

-- | Helper class for joining queries together by pattern matching on the input as a `RowList`
-- |
-- | `i` is the row containing the queries being joined.
-- | `o` is the row containing the results of those queries
-- | `il` is the `RowList` created from `i` used to iterate through its entries
class JoinQueries (i :: Row Type) (o :: Row Type) (list :: RL.RowList Type)
    | list -> i o, i -> o, o -> i where
    -- | Produces an array of `GQLQuery` records for each query in this multi-query
    -- |
    -- | The array order should match the order of items returned by `jqDecode`
    jqQueries :: Proxy list -> Record i -> Array GQLQuery
    -- | Decode each element in the given JSON array
    jqDecode :: Proxy list -> Record i -> Int -> Array Json -> Either ErrorWrapper (Record o)

-- Below, we implement `JoinQueries` by pattern matching on the `RowList` at the type level
instance jqCons :: 
    -- The tail of the list should implment `JoinQueries` as well
    ( JoinQueries itail otail tail
    -- Field must be a symbol
    , IsSymbol field
    -- Assert that `query` (the input) is the type stored at `field` in the query (input) record
    , Row.Cons field query itail i
    , Row.Lacks field itail
    -- Assert that `result` (the output) is the type stored at `field` in the output record
    , Row.Cons field result otail o
    , Row.Lacks field otail
    -- Assert that `query` is a joinable query with `result` as its output
    , JoinableQuery query result
    ) => JoinQueries i o (RL.Cons field query tail) where
    jqQueries _ queries = query `Arr.cons` rest
      where
        field = Proxy :: Proxy field
        query = getQuery $ Rec.get field queries
        rest = jqQueries (Proxy :: Proxy tail) (Rec.delete field queries)

    jqDecode _ queries idx arr = do
        -- Get this query's entry in the response. Error if missing
        json <- note (StringError "Missing response for multi-query") (arr !! idx)
        -- Decode the current query from the JSON
        let query = Rec.get field queries
        self <- (getDecode query) json
        -- Decode the remaining queries from the JSON
        rest <- jqDecode (Proxy :: Proxy tail) (Rec.delete field queries) (idx + 1) arr
        -- Set the result in the output record
        Right $ Rec.insert field self rest
      where
        field = Proxy :: Proxy field

-- This is the end case for when we reach the end of the query list
instance jqNil :: JoinQueries () () RL.Nil where
    -- No queries, no queries
    jqQueries _ _ = []
    jqDecode _ _ _ _ = Right $ {}
